import { useEffect, useMemo, useRef } from "react";
import { StepContainer } from "../components/StepContainer";
import {
  ProfessionalFields,
  type ProfessionalFieldsValues,
  NICHES,
  REGISTRY_TYPES,
} from "../components/fields/ProfessionalFields";
import { useUpdateBusinessContext } from "../hooks/useUpdateBusinessContext";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { STEPS, findStepIndexBySlug } from "../config/steps";

type Niche = (typeof NICHES)[number];
type RegistryType = (typeof REGISTRY_TYPES)[number];

export interface ProfessionalStepProps {
  onValidityChange: (valid: boolean) => void;
  onAttemptContinue: (continueFn: () => Promise<boolean>) => void;
  onMissingChange?: (labels: string[]) => void;
}

export function ProfessionalStep({
  onValidityChange,
  onAttemptContinue,
  onMissingChange,
}: ProfessionalStepProps) {
  const step = STEPS[findStepIndexBySlug("profissional")];
  const { mutateDebounced, mutate } = useUpdateBusinessContext();
  const { query } = useBusinessContext();
  const formRef = useRef<{ trigger: () => Promise<boolean>; getValues: () => ProfessionalFieldsValues } | null>(null);

  // Synchronous derivation — see IdentityStep for the race that motivated it.
  const defaults = useMemo<Partial<ProfessionalFieldsValues> | null>(() => {
    const row = query.data;
    if (!row) return null;
    return {
      niche: (NICHES as readonly string[]).includes(row.niche ?? "")
        ? (row.niche as Niche)
        : undefined,
      formations: row.formations ?? [],
      registry_type: (REGISTRY_TYPES as readonly string[]).includes(row.registry_type ?? "")
        ? (row.registry_type as RegistryType)
        : undefined,
      registry_number: row.registry_number ?? "",
      years_active: row.years_active ?? undefined,
      clients_served_estimate: row.clients_served_estimate ?? null,
    };
  }, [query.data]);

  useEffect(() => {
    onAttemptContinue(async () => {
      const ok = await formRef.current?.trigger();
      if (!ok) return false;
      const values = formRef.current!.getValues();
      await mutate({
        niche: values.niche,
        formations: values.formations,
        registry_type: values.registry_type,
        registry_number: values.registry_number,
        years_active: values.years_active,
        clients_served_estimate: values.clients_served_estimate ?? null,
      });
      return true;
    });
  }, [mutate, onAttemptContinue]);

  return (
    <StepContainer icon={step.icon} headline={step.headline} sub={step.sub}>
      {!query.isLoading ? (
        <ProfessionalFields
          key={defaults ? "loaded" : "empty"}
          defaultValues={defaults ?? undefined}
          onChange={(patch) => mutateDebounced(patch)}
          onValidityChange={onValidityChange}
          onMissingChange={onMissingChange}
          validateOnMount
          registerForm={(f) => {
            formRef.current = { trigger: f.trigger, getValues: f.getValues };
          }}
        />
      ) : null}
    </StepContainer>
  );
}
